import {useContext} from 'react';
import PropTypes from 'prop-types';

import {shstoreIdMappings} from '@stubhub/general-utils';
import {useSubscriber} from '@stubhub/pubsub-utils';

import {CookiesContext, GlobalRegistryContext} from '../context';

import {
  MYACCOUNT_EVENTS,
  MYACCOUNT_ORDER_DETAILS_EVENTS,
  MYACCOUNT_LISTINGS_EVENTS,
  SELL_FLOW_EVENTS,
} from './track-enum';
import {
  getUserGUID,
  screenView,
  use_feature,
  track_update_listing,
  delete_listings,
  beginPublish,
  successPublish,
} from './track-utils';

const TrackListener = (_props, {track}) => {
  const {gsConfig} = useContext(GlobalRegistryContext);
  const {shstoreId: storeId, currentLocale: locale} = gsConfig;
  const {site: storeCountry} = shstoreIdMappings[storeId] || {};
  const cookies = useContext(CookiesContext);
  const userGUID = getUserGUID(cookies);

  const myaTracking = {
    [MYACCOUNT_EVENTS.LOADED]: () => {
      screenView(track, {
        screenName: 'mya',
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
  };

  const myaOrderDetailsTracking = {
    [MYACCOUNT_ORDER_DETAILS_EVENTS.LOADED]: () => {
      screenView(track, {
        screenName: 'mya_order_details',
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
    [MYACCOUNT_ORDER_DETAILS_EVENTS.BASIC_LOADED]: () => {
      screenView(track, {
        screenName: 'mya_order_details_basic',
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
  };

  const myaListingsTracking = {
    [MYACCOUNT_LISTINGS_EVENTS.LOADED]: () => {
      screenView(track, {
        screenName: 'mya_listings',
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
    [MYACCOUNT_LISTINGS_EVENTS.OPEN_CHANGE_FULFILLMENT_OPTION]: (saleId) => {
      use_feature(track, {
        feature_name: 'change_ticket_medium_interaction',
        feature_details: `listing_details_button;${saleId}`,
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
    [MYACCOUNT_LISTINGS_EVENTS.CHANGE_FULFILLMENT]: ({saleId, from, to}) => {
      use_feature(track, {
        feature_name: 'change_ticket_medium',
        feature_details: `listing_details_button;${saleId};${from};${to}`,
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
    [MYACCOUNT_LISTINGS_EVENTS.PRICE_CHANGE]: ({listingId, from, to}) => {
      use_feature(track, {
        feature_name: 'change_price',
        feature_details: `price-change-field;${listingId};${from};${to}`,
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
    [MYACCOUNT_LISTINGS_EVENTS.PAYOUT_CHANGE]: ({listingId, from, to}) => {
      use_feature(track, {
        feature_name: 'change_payout',
        feature_details: `payout-change-field;${listingId};${from};${to}`,
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
    [MYACCOUNT_LISTINGS_EVENTS.STATUS_CHANGE]: ({listingId, from, to}) => {
      use_feature(track, {
        feature_name: 'change_status',
        feature_details: `status_change_button;${listingId};${from};${to}`,
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
    [MYACCOUNT_LISTINGS_EVENTS.CLICK_BUTTON]: ({featureName, cta, listingId}) => {
      use_feature(track, {
        feature_name: featureName,
        feature_details: `${cta};${listingId}`,
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
    [MYACCOUNT_LISTINGS_EVENTS.CLICK_SEARCH]: ({search_term}) => {
      use_feature(track, {
        feature_name: 'search_bar',
        feature_details: `search_bar;${search_term}`,
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
    [MYACCOUNT_LISTINGS_EVENTS.TICKET_TYPE_CHANGE]: ({listingId, from, to, cta}) => {
      use_feature(track, {
        feature_name: 'ticket_type',
        feature_details: `ticket-type-dropdown;${listingId};${from};${to}`,
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
    [MYACCOUNT_LISTINGS_EVENTS.QUANTITY_CHANGE]: ({listingId, from, to, cta}) => {
      const CTA = cta === 'dropdown' ? 'Quantity Dropdown' : 'Quantity Input';
      use_feature(track, {
        feature_name: 'change_quantity',
        feature_details: `${CTA};${listingId};${from};${to}`,
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
    [MYACCOUNT_LISTINGS_EVENTS.DELETE_LISTING]: ({listingId}) => {
      use_feature(track, {
        feature_name: 'confirm_delete_listing',
        feature_details: `Confirm Delete Listing;${listingId}`,
        storeId,
        storeCountry,
        locale,
        userGUID,
      });
    },
    [MYACCOUNT_LISTINGS_EVENTS.BULK_UPDATE]: ({field, change, listingIds}) => {
      track_update_listing(track, {
        field,
        change,
        listingIds,
      });
    },
    [MYACCOUNT_LISTINGS_EVENTS.BULK_ACTIONS_DELETE_LISTINGS]: ({listingIds}) => {
      delete_listings(track, {
        storeId,
        storeCountry,
        locale,
        userGUID,
        listingIds,
      });
    },
  };

  const sellFlowTracking = {
    [SELL_FLOW_EVENTS.LISTING_DETAILS_LOADED]: ({
      eventId,
      eventName,
      countryCode,
      daysToEvent,
      genre,
      inventoryMarket,
      ancestors,
      fulfillmentMethods,
      sellFlowStart,
      eventDateUTC,
      onSaleDate,
      imageURL,
    }) => {
      beginPublish(track, {
        eventId,
        eventName,
        countryCode,
        daysToEvent,
        genre,
        inventoryMarket,
        ancestors,
        fulfillmentMethods,
        sellFlowStart,
        onSaleDate,
        imageURL,
        eventDateUTC,
      });
    },
    [SELL_FLOW_EVENTS.LISTING_CREATED]: ({
      eventId,
      eventName,
      countryCode,
      daysToEvent,
      genre,
      inventoryMarket,
      ancestors,
      fulfillmentMethods,
      medium,
      fulFillmentMethod,
      deliveryNow,
      numTickets,
      faceValue,
      ticketPrice,
      ticketCurrency,
      numFeatures,
      connections,
      numRestrictions,
      sellerType,
      paymenMethod,
      sellFlowStart,
      listingId,
      eventDateUTC,
      onSaleDate,
      imageURL,
    }) => {
      successPublish(track, {
        eventId,
        eventName,
        countryCode,
        daysToEvent,
        genre,
        inventoryMarket,
        ancestors,
        fulfillmentMethods,
        medium,
        fulFillmentMethod,
        deliveryNow,
        numTickets,
        faceValue,
        ticketPrice,
        ticketCurrency,
        numFeatures,
        connections,
        numRestrictions,
        sellerType,
        paymenMethod,
        sellFlowStart,
        listingId,
        onSaleDate,
        imageURL,
        eventDateUTC,
      });
    },
  };

  useSubscriber(MYACCOUNT_EVENTS.MAIN, myaTracking);
  useSubscriber(MYACCOUNT_ORDER_DETAILS_EVENTS.MAIN, myaOrderDetailsTracking);
  useSubscriber(MYACCOUNT_LISTINGS_EVENTS.MAIN, myaListingsTracking);
  useSubscriber(SELL_FLOW_EVENTS.MAIN, sellFlowTracking);
  return null;
};

TrackListener.contextTypes = {
  track: PropTypes.object,
};

export default TrackListener;
